import type { Answer, Facts } from './facts';
import type { Question, QuestionSet } from './questions';

export interface RunState {
  facts: Facts;
  asked: string[];
  /**
   * The state this one was answered from, absent at the start of a run.
   *
   * Undo is a link rather than a replay because every state here is already immutable:
   * `answer` builds a new one and never touches the old, so the previous state is still
   * exactly correct and holding onto it costs one reference. Replaying instead would
   * mean storing each choice and re-running `apply` in order, which is the same result
   * arrived at by more moving parts.
   */
  previous?: RunState;
}

/**
 * The next question to put on screen, or null when we're ready to search.
 *
 * This replaces the old app's `switch (counter)` entirely: a question is eligible
 * when its `when` predicate passes and it hasn't been asked. There is no jump table
 * to get out of sync, so states like the old `counter === 2` (unreachable) or
 * `counter === 7`'s card2 branch (dead end) cannot exist. Both food trees ride on the
 * same rule - their gates are exact path matches, so exactly one node per tree is ever
 * live, and the routing question decides which tree that is.
 */
export function nextQuestion(set: QuestionSet, state: RunState): Question | null {
  if (state.asked.length >= set.maxQuestions) return null;
  return (
    set.questions.find(
      (q) => !state.asked.includes(q.id) && (q.when?.(state.facts) ?? true),
    ) ?? null
  );
}

export function answer(
  set: QuestionSet,
  state: RunState,
  questionId: string,
  choice: Answer,
): RunState {
  const question = set.questions.find((q) => q.id === questionId);
  if (!question) {
    throw new Error(`Unknown question: ${questionId}`);
  }
  if (state.asked.includes(questionId)) {
    throw new Error(`Question already answered: ${questionId}`);
  }
  return {
    facts: question.apply[choice](state.facts),
    asked: [...state.asked, questionId],
    previous: state,
  };
}

export function canGoBack(state: RunState): boolean {
  return state.previous !== undefined;
}

/**
 * One step back, or the same state when there is nowhere to go.
 *
 * Undoing the answer undoes everything it established - a cuisine descent gives back
 * the node it entered, a skip reopens the axis it closed - because the previous state
 * is the whole `Facts` value from before, not a patch to reverse.
 */
export function back(state: RunState): RunState {
  return state.previous ?? state;
}

export function isComplete(set: QuestionSet, state: RunState): boolean {
  return nextQuestion(set, state) === null;
}

/**
 * How far through we are, for the progress dots. The denominator is an estimate -
 * the tree is adaptive, so the real total isn't knowable up front. Clamped so the
 * indicator never goes backwards or overflows.
 */
export function progress(
  set: QuestionSet,
  state: RunState,
): { asked: number; estimatedTotal: number } {
  const asked = state.asked.length;
  return {
    asked,
    estimatedTotal: Math.max(asked, Math.min(set.maxQuestions, set.estimatedQuestions)),
  };
}
